import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Structured loading placeholders that mirror the layout they stand in
 * for, so pages keep their shape while data loads instead of showing a
 * single gray block. Widths vary deterministically (no randomness — the
 * pages render on the server too) to read as content, not stripes.
 */

const widths = ["w-3/4", "w-2/3", "w-4/5", "w-1/2", "w-3/5"];
const w = (i: number) => widths[i % widths.length];

export function DirectoryGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="h-full">
          <CardContent className="flex gap-3">
            <Skeleton className="size-12 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className={cn("h-4", w(i))} />
              <Skeleton className={cn("h-3.5", w(i + 2))} />
              <div className="flex gap-1 pt-1">
                <Skeleton className="h-5 w-14 rounded-md" />
                <Skeleton className="h-5 w-16 rounded-md" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

export function RowListSkeleton({
  count = 5,
  withAvatar = true,
}: {
  count?: number;
  withAvatar?: boolean;
}) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="flex items-start gap-3">
            {withAvatar && (
              <Skeleton className="mt-0.5 size-9 shrink-0 rounded-full" />
            )}
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className={cn("h-4", w(i))} />
              <Skeleton className={cn("h-3.5", w(i + 3))} />
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

export function TableSkeleton({
  rows = 8,
  columns = 5,
  className,
}: {
  rows?: number;
  columns?: number;
  className?: string;
}) {
  return (
    <div className={cn("overflow-hidden rounded-xl ring-1 ring-foreground/10", className)}>
      <div className="bg-muted/50 flex gap-4 border-b px-4 py-3">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3.5 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 border-b px-4 py-3.5 last:border-b-0">
          {Array.from({ length: columns }).map((_, c) => (
            <div key={c} className="flex-1">
              <Skeleton className={cn("h-3.5", w(r + c))} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function StatGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="size-4 rounded" />
            <Skeleton className="mt-1 h-8 w-16" />
            <Skeleton className={cn("h-3.5", w(i))} />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="max-w-5xl">
      <Skeleton className="h-7 w-44" />
      <Skeleton className="mt-2 h-4 w-72" />
      <div className="mt-6">
        <StatGridSkeleton />
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3.5 w-56" />
            </CardHeader>
            <CardContent className="space-y-3">
              {Array.from({ length: 4 }).map((_, r) => (
                <Skeleton key={r} className={cn("h-3.5", w(i + r))} />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function DetailSkeleton() {
  return (
    <div className="max-w-3xl">
      <Skeleton className="h-4 w-32" />
      <div className="mt-6 flex items-center gap-4">
        <Skeleton className="size-20 shrink-0 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-28" />
        </div>
      </div>
      <div className="mt-4 flex gap-1.5">
        <Skeleton className="h-5 w-20 rounded-md" />
        <Skeleton className="h-5 w-16 rounded-md" />
        <Skeleton className="h-5 w-24 rounded-md" />
      </div>
      {Array.from({ length: 2 }).map((_, i) => (
        <Card key={i} className="mt-4 first:mt-6">
          <CardHeader>
            <Skeleton className="h-4 w-24" />
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className={cn("h-3.5", w(i))} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ThreadSkeleton() {
  return (
    <div className="max-w-5xl">
      <Skeleton className="h-4 w-32" />
      <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_280px]">
        <div>
          <Skeleton className="h-7 w-3/4" />
          <div className="mt-2 flex gap-1">
            <Skeleton className="h-5 w-16 rounded-md" />
            <Skeleton className="h-5 w-20 rounded-md" />
          </div>
          <Card className="mt-4">
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Skeleton className="size-8 rounded-full" />
                <Skeleton className="h-4 w-44" />
              </div>
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-2/3" />
            </CardContent>
          </Card>
          <div className="mt-4 space-y-3">
            <RowListSkeleton count={2} />
          </div>
        </div>
        <div>
          <Card>
            <CardHeader>
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-3/4" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export function FormSkeleton({ sections = 3 }: { sections?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: sections }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3.5 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: 3 }).map((_, r) => (
              <div key={r} className="flex items-center justify-between gap-4">
                <div className="flex-1 space-y-1.5">
                  <Skeleton className={cn("h-4", w(i + r))} />
                  <Skeleton className="h-3 w-2/3" />
                </div>
                <Skeleton className="h-5 w-9 rounded-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ArticleSkeleton() {
  return (
    <div>
      <Skeleton className="h-8 w-2/3" />
      <div className="mt-6 space-y-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton
            key={i}
            className={cn("h-3.5", i % 4 === 3 ? w(i) : "w-full")}
          />
        ))}
      </div>
    </div>
  );
}
